import { useEffect, useState } from 'react';
import { ArrowLeft, Menu, X } from 'lucide-react';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

export interface BreadcrumbItem {
  label: string;
  /** Если href не задан — render как текущая страница (не ссылка). */
  href?: string;
}

interface Props {
  title: string;
  /** Если задан — рендерит иконку-бургер слева от logo (на <lg).
   *  Открывает drawer с TOC. На desktop (≥lg) sidebar TOC уже видим, бургер скрыт. */
  onTocOpen?: () => void;
  /** Кастомные breadcrumbs (вместо дефолтных «Правовые документы › title»).
   *  Пример: [{label: 'Аккаунт', href: '/account'}, {label: 'Cookie'}]. */
  breadcrumbs?: BreadcrumbItem[];
}

/**
 * Sticky top header — переиспользуется на legal-страницах и в /account/*.
 * - Smart back-button: history если same-origin, иначе close-tab если из target=_blank, иначе /.
 * - Logo→home слева рядом с back.
 * - Breadcrumbs: дефолтные «Главная › Правовые документы › {title}» ИЛИ кастомные через prop.
 * - ThemeToggle справа.
 */
export function LegalHeader({ title, onTocOpen, breadcrumbs }: Props) {
  const [isPopup, setIsPopup] = useState(false);

  useEffect(() => {
    // Если страница открыта через target="_blank" из той же вкладки — referrer пустой,
    // history.length 1. window.opener!=null значит можно close().
    const opener = typeof window !== 'undefined' ? window.opener : null;
    const noHistory = typeof window !== 'undefined' && window.history.length <= 1;
    const noReferrer = typeof document !== 'undefined' && !document.referrer;
    setIsPopup(Boolean(opener) || (noHistory && noReferrer));
  }, []);

  const handleBack = () => {
    if (isPopup && typeof window !== 'undefined' && window.opener) {
      window.close();
      return;
    }
    if (typeof document !== 'undefined' && document.referrer) {
      try {
        const ref = new URL(document.referrer);
        if (ref.origin === window.location.origin) {
          window.history.back();
          return;
        }
      } catch {
        // fall through
      }
    }
    window.location.href = '/';
  };

  const backLabel = isPopup ? 'Закрыть' : 'Назад';
  const BackIcon = isPopup ? X : ArrowLeft;

  return (
    <header data-print-hide className="sticky top-0 z-40 border-b border-border bg-surface-canvas/95 backdrop-blur supports-[backdrop-filter]:bg-surface-canvas/80">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 md:px-8">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-sm text-text-secondary hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-colors"
          aria-label={backLabel}
        >
          <BackIcon className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">{backLabel}</span>
        </button>

        {onTocOpen && (
          <button
            type="button"
            onClick={onTocOpen}
            aria-label="Открыть оглавление"
            className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md text-text-secondary hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-colors"
          >
            <Menu className="h-4 w-4" aria-hidden />
          </button>
        )}

        <a href="/" className="flex items-center gap-2 font-display text-base font-semibold text-text-primary hover:text-text-link transition-colors">
          <span className="text-text-link">AI</span>
          <span>AgentCEO</span>
        </a>

        <nav aria-label="Breadcrumb" className="hidden md:flex items-center gap-1.5 text-xs text-text-secondary ml-2">
          {breadcrumbs ? (
            breadcrumbs.map((item, idx) => (
              <span key={`${item.label}-${idx}`} className="flex items-center gap-1.5">
                <span>›</span>
                {item.href ? (
                  <a href={item.href} className="hover:underline">{item.label}</a>
                ) : (
                  <span className="text-text-primary line-clamp-1">{item.label}</span>
                )}
              </span>
            ))
          ) : (
            <>
              <span>›</span>
              <a href="/legal" className="hover:underline">Правовые документы</a>
              {title && (
                <>
                  <span>›</span>
                  <span className="text-text-primary line-clamp-1">{title}</span>
                </>
              )}
            </>
          )}
        </nav>

        <ThemeToggle className="ml-auto shrink-0" />
      </div>
    </header>
  );
}
